import React, { useState } from "react";
import { Check } from "lucide-react";
import DocSidebar from "../components/DocSidebar";
import CodeBlock from "../components/CodeBlock";
import MethodBadge from "../components/MethodBadge";
import Keyword from "../components/Keyword";

const sidebarItems = [
  { id: "tts-overview-nav", label: "Getting Started", type: "category" },
  { id: "tts-overview", label: "Overview", type: "item", indent: true },

  { id: "tts-getting-started", label: "Getting Started", type: "heading" },
  { id: "tts-base-url", label: "Base URL", type: "item", indent: true },
  { id: "tts-auth-header", label: "Authentication Header", type: "item", indent: true },

  { id: "tts-endpoints", label: "API Endpoints", type: "heading" },
  { id: "tts-generate-speech", label: "1. Synthesize Speech (/with-api-key)", type: "item", indent: true },
  { id: "tts-get-voices", label: "2. Get Available Voices", type: "item", indent: true },
  { id: "tts-request-header", label: "Request Headers", type: "item", indent: true },
  { id: "tts-request-body", label: "Request Body (Payload)", type: "item", indent: true },

  { id: "tts-language-reference", label: "Supported Languages & Voices", type: "heading" },
  { id: "tts-supported-languages", label: "Supported Languages", type: "item", indent: true },

  { id: "tts-code-examples", label: "Code Examples", type: "heading" },
  { id: "tts-python", label: "Python", type: "item", indent: true },
  { id: "tts-curl", label: "cURL", type: "item", indent: true },
  { id: "tts-javascript", label: "JavaScript (Fetch)", type: "item", indent: true },

  { id: "tts-response-format", label: "Response Format", type: "heading" },
  { id: "tts-success-response", label: "Success Response", type: "item", indent: true },
  { id: "tts-error-responses", label: "Error Responses", type: "item", indent: true },

  { id: "tts-best-practices", label: "Best Practices", type: "heading" },
  { id: "tts-next-steps", label: "Next Steps", type: "heading" },
];

const TextToSpeechTab = () => {
  const [activeSection, setActiveSection] = useState("tts-overview");

  return (
    <div className="flex flex-col lg:flex-row gap-8 max-w-[1440px] mx-auto px-2 sm:px-6 py-6 sm:py-10 relative">
      {/* Sidebar Navigation */}
      <DocSidebar
        items={sidebarItems}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      {/* Main Content */}
      <div className="flex flex-col gap-16 flex-1 lg:max-w-none xl:px-10">
        {/* Header */}
        <section id="tts-overview-nav" className="flex flex-col gap-4">
          <div className="inline-block">
            <span className="font-black text-sm uppercase tracking-[0.2em] text-[#17A257] bg-[#E7F6EC] px-3 py-1 rounded-full">
              Text-to-Speech API
            </span>
          </div>
          <div id="tts-overview" className="flex flex-col gap-4">
            <h1 className="font-black text-3xl lg:text-4xl leading-tight text-[#1D2129]">
              Quickstart Guide For Text-to-Speech (TTS) API
            </h1>
            <p className="font-medium text-lg leading-relaxed text-[#7E726B]">
              The NKENNE AI Text-to-Speech API synthesizes natural, lifelike, and expressive spoken audio from text in African languages and English using an API key. It provides customizable voice models and adjustable speaking rates.
            </p>
          </div>
        </section>

        {/* Getting Started Section */}
        <div className="flex flex-col gap-20">
          <section id="tts-getting-started" className="flex flex-col gap-6 w-full">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Getting Started
            </h2>

            <div className="flex flex-col gap-6">
              {/* Base URL */}
              <div id="tts-base-url" className="flex flex-col gap-2">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Base URL
                </h3>
                <div className="bg-[#F4F8F7] p-3 rounded-xl border border-[#D0E7DF] inline-block w-fit">
                  <p className="font-mono font-medium text-base text-[#1E6C56]">
                    https://developers-api.nkenne.ai
                  </p>
                </div>
              </div>

              {/* Authentication Header */}
              <div id="tts-auth-header" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Authentication Header
                </h3>
                <p className="font-['Inter'] font-medium text-base text-[#7E726B]">
                  Pass your API key in the <Keyword>Authorization</Keyword> header with all requests:
                </p>
                <div className="bg-[#1D2129] p-4 rounded-xl font-mono text-sm text-white overflow-hidden w-fit">
                  <span className="text-[#BC39B7]">Authorization</span>:{" "}
                  <span className="text-[#17A257]">YOUR_API_KEY</span>
                </div>
              </div>
            </div>
          </section>

          {/* API Endpoints */}
          <section id="tts-endpoints" className="flex flex-col gap-10">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              API Endpoints
            </h2>

            {/* 1. Generate Speech with API Key */}
            <div id="tts-generate-speech" className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h3 className="font-['Axiforma'] font-black text-xl text-[#000000]">
                  1. Synthesize Speech (with API Key)
                </h3>
                <p className="text-sm text-[#7E726B]">
                  Synthesize input text into high-fidelity streaming audio in the target language using your API key.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <MethodBadge method="POST" />
                <span className="font-mono text-sm font-semibold text-[#1D2129]">
                  /api/tts/v1/{`{language}`}/with-api-key
                </span>
              </div>

              <div className="flex flex-col gap-2">
                <h4 className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  Full Endpoint URL
                </h4>
                <p className="font-mono text-sm font-medium text-[#1E6C56] bg-[#F4F8F7] p-3 rounded-xl border border-[#D0E7DF] inline-block w-fit">
                  https://developers-api.nkenne.ai/api/tts/v1/{`{language}`}/with-api-key
                </p>
              </div>

              {/* Request Headers */}
              <div id="tts-request-header" className="flex flex-col gap-4">
                <h4 className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  Request Headers
                </h4>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base text-[#000000]">
                          Header
                        </th>
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base text-[#000000]">
                          Required
                        </th>
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base text-[#000000]">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#E0E4E2]">
                        <td className="p-3">
                          <Keyword>Authorization</Keyword>
                        </td>
                        <td className="p-3">
                          <Check className="w-5 h-5 text-[#17A257]" />
                        </td>
                        <td className="p-3 text-sm text-[#000000]">
                          YOUR_API_KEY
                        </td>
                      </tr>
                      <tr>
                        <td className="p-3">
                          <Keyword>Content-Type</Keyword>
                        </td>
                        <td className="p-3">
                          <Check className="w-5 h-5 text-[#17A257]" />
                        </td>
                        <td className="p-3 text-sm">
                          <Keyword color="#0070C1">application/json</Keyword>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Body */}
              <div id="tts-request-body" className="flex flex-col gap-4">
                <h4 className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  Request Body (JSON)
                </h4>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Field</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Type</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Required</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#E0E4E2]">
                        <td className="p-3"><Keyword color="#BC39B7">text</Keyword></td>
                        <td className="p-3 text-sm font-mono text-gray-600">string</td>
                        <td className="p-3"><Check className="w-5 h-5 text-[#17A257]" /></td>
                        <td className="p-3 text-sm">The text content to convert to speech.</td>
                      </tr>
                      <tr className="border-b border-[#E0E4E2]">
                        <td className="p-3"><Keyword color="#BC39B7">voice_name</Keyword></td>
                        <td className="p-3 text-sm font-mono text-gray-600">string</td>
                        <td className="p-3 text-sm text-gray-400">Optional</td>
                        <td className="p-3 text-sm">Specific voice identifier (defaults to language default).</td>
                      </tr>
                      <tr>
                        <td className="p-3"><Keyword color="#BC39B7">speaking_rate</Keyword></td>
                        <td className="p-3 text-sm font-mono text-gray-600">number</td>
                        <td className="p-3 text-sm text-gray-400">Optional</td>
                        <td className="p-3 text-sm">Audio playback speed multiplier (default: 1.0, range: 0.5 - 2.0).</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-2">
                  <CodeBlock
                    id="tts-payload-sample"
                    language="json"
                    code={`{
  "text": "Ẹ ku aarọ o, bawo ni gbogbo nkan?",
  "voice_name": "yoruba_female_1",
  "speaking_rate": 1.0
}`}
                  />
                </div>
              </div>
            </div>

            {/* 2. Get Available Voices */}
            <div id="tts-get-voices" className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <h3 className="font-['Axiforma'] font-black text-xl text-[#000000]">
                  2. Get Available Voices
                </h3>
                <p className="text-sm text-[#7E726B]">
                  Retrieve the list of supported voice models and default voice for a specific language.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <MethodBadge method="GET" />
                <span className="font-mono text-sm font-semibold text-[#1D2129]">
                  /api/tts/v1/{`{language}`}/voices
                </span>
              </div>

              <div className="flex flex-col gap-2">
                <p className="font-bold text-sm text-[#1D2129]">Example Response (JSON):</p>
                <CodeBlock
                  id="tts-voices-response-sample"
                  language="json"
                  code={`{
  "language": "yoruba",
  "default_voice": "yoruba_female_1",
  "voices": [
    {
      "id": "yoruba_female_1",
      "name": "Yoruba Female (Standard)",
      "gender": "Female"
    },
    {
      "id": "yoruba_male_1",
      "name": "Yoruba Male (Standard)",
      "gender": "Male"
    }
  ]
}`}
                />
              </div>
            </div>
          </section>

          {/* Supported Languages & Voices */}
          <section id="tts-language-reference" className="flex flex-col gap-6">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Supported Languages & Voices
            </h2>

            <div id="tts-supported-languages" className="flex flex-col gap-4">
              <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                      <th className="text-left p-3 font-bold text-sm text-[#000000]">Language</th>
                      <th className="text-left p-3 font-bold text-sm text-[#000000]">URL Slug</th>
                      <th className="text-left p-3 font-bold text-sm text-[#000000]">Available Voices</th>
                      <th className="text-left p-3 font-bold text-sm text-[#000000]">Output Format</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { lang: "Yoruba", slug: "yoruba", voices: "Female, Male", format: "MP3 / WAV (24kHz)" },
                      { lang: "Swahili", slug: "swahili", voices: "Female, Male", format: "MP3 / WAV (24kHz)" },
                      { lang: "Nigerian Pidgin", slug: "nigerian-pidgin", voices: "Female, Male", format: "MP3 / WAV (24kHz)" },
                      { lang: "English", slug: "english", voices: "Female, Male", format: "MP3 / WAV (24kHz)" },
                    ].map((row, index) => (
                      <tr key={index} className={index < 3 ? "border-b border-[#E0E4E2]" : ""}>
                        <td className="p-3 font-medium text-sm text-[#000000]">{row.lang}</td>
                        <td className="p-3"><Keyword color="#0070C1">{row.slug}</Keyword></td>
                        <td className="p-3 text-sm text-gray-700">{row.voices}</td>
                        <td className="p-3 text-sm text-gray-700">{row.format}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Code Examples */}
          <section id="tts-code-examples" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Code Examples
            </h2>

            <div className="flex flex-col gap-8">
              {/* Python Example */}
              <div id="tts-python" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-lg text-[#000000]">
                  Python
                </p>
                <CodeBlock
                  id="tts-python-example"
                  language="python"
                  code={`import requests

API_URL = "https://developers-api.nkenne.ai/api/tts/v1/yoruba/with-api-key"
API_KEY = "your_api_key_here"

payload = {
    "text": "Ẹ ku aarọ o, bawo ni gbogbo nkan?",
    "voice_name": "yoruba_female_1",
    "speaking_rate": 1.0
}

headers = {
    "Authorization": API_KEY,
    "Content-Type": "application/json"
}

response = requests.post(API_URL, headers=headers, json=payload)
response.raise_for_status()

# Save binary audio response to file
with open("output.mp3", "wb") as f:
    f.write(response.content)

print("Audio saved successfully to output.mp3")`}
                />
              </div>

              {/* cURL Example */}
              <div id="tts-curl" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  cURL
                </p>
                <CodeBlock
                  id="tts-curl-example"
                  language="bash"
                  code={`curl -X POST https://developers-api.nkenne.ai/api/tts/v1/yoruba/with-api-key \\
  -H "Authorization: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "text": "Ẹ ku aarọ o, bawo ni gbogbo nkan?",
    "voice_name": "yoruba_female_1",
    "speaking_rate": 1.0
  }' \\
  --output speech.mp3`}
                />
              </div>

              {/* JavaScript Example */}
              <div id="tts-javascript" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  JavaScript (Fetch)
                </p>
                <CodeBlock
                  id="tts-javascript-example"
                  language="javascript"
                  code={`const response = await fetch(
  "https://developers-api.nkenne.ai/api/tts/v1/yoruba/with-api-key",
  {
    method: "POST",
    headers: {
      "Authorization": "YOUR_API_KEY",
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      text: "Ẹ ku aarọ o, bawo ni gbogbo nkan?",
      voice_name: "yoruba_female_1",
      speaking_rate: 1.0
    })
  }
);

if (!response.ok) {
  throw new Error("Speech synthesis failed");
}

const audioBlob = await response.blob();
const audioUrl = URL.createObjectURL(audioBlob);
const audio = new Audio(audioUrl);
audio.play();`}
                />
              </div>
            </div>
          </section>

          {/* Response Format */}
          <section id="tts-response-format" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Response Format
            </h2>

            <div className="flex flex-col gap-10">
              {/* Success Response */}
              <div id="tts-success-response" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Success Response (200 OK)
                </h3>
                <p className="text-sm text-[#7E726B]">
                  The API returns a binary audio stream with Content-Type <Keyword color="#0070C1">audio/mpeg</Keyword> or <Keyword color="#0070C1">audio/wav</Keyword>.
                </p>
              </div>

              {/* Error Responses */}
              <div id="tts-error-responses" className="flex flex-col gap-6">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Error Responses
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Invalid API Key */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Unauthorized (401)
                    </p>
                    <CodeBlock
                      id="tts-error-401"
                      language="json"
                      code={`{
  "error": "Unauthorized: Invalid API Key"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      API key is missing, expired, or unauthorized for the Text-to-Speech service.
                    </p>
                  </div>

                  {/* Missing Text */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Bad Request (400)
                    </p>
                    <CodeBlock
                      id="tts-error-400"
                      language="json"
                      code={`{
  "error": "Text payload is required and cannot be empty"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      Occurs when the text parameter is missing or empty.
                    </p>
                  </div>

                  {/* Unsupported Language */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Not Found (404)
                    </p>
                    <CodeBlock
                      id="tts-error-404"
                      language="json"
                      code={`{
  "error": "Voice model or language not found"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      Occurs when specifying an invalid language slug or unknown voice model ID.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section id="tts-best-practices" className="flex flex-col gap-6">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Best Practices
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: "Query Voices Dynamically", desc: "Use the /api/tts/v1/{language}/voices endpoint to inspect available voices dynamically rather than hardcoding IDs." },
                { title: "Optimize Text Chunks", desc: "For long articles or books, synthesize text in paragraph-sized chunks for optimal latency and streaming performance." },
                { title: "Audio Caching", desc: "Cache commonly spoken phrases (e.g. greetings, common prompts) to save character tokens and speed up user response times." },
                { title: "Punctuation & Diacritics", desc: "Include proper punctuation and tone marks (e.g. Yoruba diacritics) for maximum pronunciation accuracy." },
              ].map((practice, index) => (
                <div key={index} className="p-4 rounded-xl border border-[#E0E4E2] bg-white">
                  <h4 className="font-bold text-base text-[#1D2129] mb-1">{practice.title}</h4>
                  <p className="text-sm text-[#7E726B]">{practice.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Next Steps */}
          <section id="tts-next-steps" className="flex flex-col gap-6 mb-12">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Next Steps
            </h2>
            <div className="flex flex-col gap-3">
              {[
                "Test different voice styles and speaking rates in the Speech Studio",
                "Integrate real-time speech synthesis into mobile applications, chatbots, and IVR systems",
                "Combine with Speech-to-Text for seamless voice-to-voice interactive conversations",
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#17A257]" />
                  <p className="font-medium text-base text-[#1D2129]">{step}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TextToSpeechTab;
